import { AccountWithTransactions } from "@common/dto/reports/get-account-transactions-report-response.dto";
import { GLViewRow } from "./glViewTypes";

const MAX_TRANSACTIONS_COUNT_TO_EXPAND = 20;
const MAX_ACCOUNTS_COUNT_TO_AUTO_EXPAND = 5;

export function getGLExpandedState(
    data: AccountWithTransactions[],
): Record<string, boolean> {
    return data.reduce<Record<string, boolean>>(
        (state, { account, entityIds }) => {
            state[`gl:account:${rowScope(account.code, entityIds)}`] = true;
            return state;
        },
        {},
    );
}

function rowScope(code: number, entityIds: number[]): string {
    return `${code}:${entityIds.join(",")}`;
}

export function shouldAutoExpandGL(data: AccountWithTransactions[]): boolean {
    if (data.length <= MAX_ACCOUNTS_COUNT_TO_AUTO_EXPAND) {
        return true;
    }

    return data.every(
        ({ transactions }) =>
            transactions.transactions.length < MAX_TRANSACTIONS_COUNT_TO_EXPAND,
    );
}

/**
 * Converts AccountWithTransactions[] into a nested row model for ViewTable.
 * Each account becomes a parent row; its children are:
 *   - an openingBalance row
 *   - one transaction row per journal entry line
 *   - a closingBalance row
 */
export function buildGLRows(data: AccountWithTransactions[]): GLViewRow[] {
    return data.map(
        ({ account, entityIds, transactions, isAccountIncreasingByDebit }) => {
            const scope = rowScope(account.code, entityIds);
            const children: GLViewRow[] = [
                {
                    id: `gl:opening:${scope}`,
                    rowKind: "openingBalance",
                    balanceValue: transactions.openingBalance,
                },
                ...transactions.transactions.map((txn, index) => ({
                    id: `gl:txn:${scope}:${txn.referenceNumber}:${index}`,
                    rowKind: "transaction" as const,
                    transaction: txn,
                    isAccountIncreasingByDebit,
                })),
                {
                    id: `gl:closing:${scope}`,
                    rowKind: "closingBalance",
                    balanceValue: transactions.closingBalance,
                },
            ];

            return {
                id: `gl:account:${scope}`,
                rowKind: "account" as const,
                account,
                entityIds,
                isAccountIncreasingByDebit,
                closingBalance: transactions.closingBalance,
                children,
            };
        },
    );
}
